/**
 * IFRAP-AI - Page des credentials presse
 * Gestion des abonnements journaux
 */

import React, { useState, useEffect } from 'react';
import { 
  Newspaper, Key, RefreshCw, Check, X, AlertTriangle,
  ExternalLink, Clock, Shield
} from 'lucide-react';
import api from '../../api/client';

const NewspaperCredentialsPage = () => {
  const [credentials, setCredentials] = useState([]);
  const [warning, setWarning] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await api.get('/api/newspapers/credentials');
      setCredentials(response.data.credentials || []);
      setWarning(response.data.warning || '');
      setLastUpdated(response.data.last_updated || '');
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const testAllCredentials = async () => {
    setTesting(true);
    setTestResults(null);
    
    try {
      const response = await api.post('/api/newspapers/test');
      setTestResults(response.data);
      // Refresh credentials to get updated status
      await fetchCredentials();
    } catch (error) {
      console.error('Error testing credentials:', error);
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ok':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <Check className="w-3 h-3" />
            OK
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            <X className="w-3 h-3" />
            Échec
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            Non testé
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="w-8 h-8 border-4 border-[#000091]/20 border-t-[#000091] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-[#000091]" />
          Abonnements Presse
        </h1>
        <p className="text-gray-500 mt-1">
          Gestion des credentials pour accéder aux articles payants
        </p>
      </div>

      {/* Warning banner */}
      {warning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">⚠️ ABONNEMENTS JBGIRAUD À REMPLACER</p>
            <p className="text-sm text-yellow-700 mt-1">{warning}</p>
          </div>
        </div>
      )}

      {/* Test button */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={testAllCredentials}
          disabled={testing}
          className="flex items-center gap-2 px-4 py-2 bg-[#000091] text-white rounded-lg hover:bg-[#000091]/90 disabled:opacity-50 transition-colors"
          data-testid="test-all-credentials"
        >
          {testing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Test en cours...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Tester tous les credentials
            </>
          )}
        </button>
        
        {lastUpdated && (
          <span className="text-sm text-gray-500">
            Dernière mise à jour: {lastUpdated}
          </span>
        )}
      </div>

      {/* Test results summary */}
      {testResults && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-2">Résultats des tests</h3>
          <div className="flex gap-4 text-sm">
            <span className="text-green-600">
              ✓ {testResults.summary.successful} réussi(s)
            </span>
            <span className="text-red-600">
              ✗ {testResults.summary.failed} échec(s)
            </span>
            <span className="text-gray-500">
              Total: {testResults.summary.total_tested}
            </span>
          </div>
        </div>
      )}

      {/* Credentials list */}
      <div className="space-y-4">
        {credentials.map((cred) => (
          <div
            key={cred.id}
            className="bg-white border border-gray-200 rounded-lg p-5"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-gray-600" />
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900">{cred.name}</h3>
                  
                  {/* Email(s) */}
                  <div className="mt-1 space-y-1">
                    {cred.emails ? (
                      cred.emails.map((email, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <Key className="w-3.5 h-3.5" />
                          <span>{email}</span>
                          <span className="text-gray-400">(Option {idx + 1})</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Key className="w-3.5 h-3.5" />
                        <span>{cred.email}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Note */}
                  {cred.note && (
                    <p className="mt-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded inline-block">
                      {cred.note}
                    </p>
                  )}
                  
                  {/* Last test */}
                  {cred.last_test && (
                    <p className="mt-2 text-xs text-gray-400">
                      Dernier test: {new Date(cred.last_test).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-2">
                {getStatusBadge(cred.status)}
              </div>
            </div>
            
            {/* Test result detail */}
            {testResults?.results?.[cred.id] && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {testResults.results[cred.id].tests ? (
                  // Le Monde with multiple tests
                  <div className="space-y-2">
                    {testResults.results[cred.id].tests.map((test, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Option {test.option}: {test.email}</span>
                        {test.success ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-4 h-4" />
                            {test.message}
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1">
                            <X className="w-4 h-4" />
                            {test.error}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Single test
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Résultat du test:</span>
                    {testResults.results[cred.id].success ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {testResults.results[cred.id].message}
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center gap-1">
                        <X className="w-4 h-4" />
                        {testResults.results[cred.id].error}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info box */}
      <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Note sur les tests automatiques</p>
            <p className="mt-1">
              Les tests automatiques peuvent échouer même avec des credentials valides 
              si le site utilise des protections anti-bot (Captcha, Cloudflare). 
              En cas d'échec, testez manuellement dans un navigateur.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewspaperCredentialsPage;
